import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/queryParamUtils';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { orderedProductLevels, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { NavItemEnum } from '../../constants/routing/NavItems';
import Screens from '../../constants/routing/Screens';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { usePageParams } from '../../hooks/usePageParams';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import HeaderLayout from '../layout/HeaderLayout';
import ProductInfoContainer from '../common/ProductInfoContainer';
import FailedLoadBanner from '../common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../common/loading/FallbackErrorBanner';
import { SuiteCalculator } from '../calculator/SuiteCalculator';
import ValuePropsSection from '../common/ValuePropsSection';
import { SuiteTiles } from '../merch-card/SuiteTiles';
import CompareStatusFooter from '../compare/CompareStatusFooter';
import { getMerchCardsToShow } from '../../constants/merchCards/MerchCardsToShow';
import IntegrationsSection from '../common/Integrations';
import CodeSplitEnvWrapper from '../common/env/CodeSplitEnvWrapper';
import Faq from '../faqs/Faq';
import { FaqsConfig } from '../../constants/faqs/FaqsConfig';
import Question from '../faqs/Question';
import FooterMicrocopy from '../faqs/FooterMicrocopy';
import WhyGoHubSpotSection from '../free-tools-page/WhyGoHubSpotSection';
import ProductPageTitle, { ProductPageHeader } from '../product-pages/ProductPageTitle';
import ProductPageSubtitle from '../product-pages/ProductPageSubtitle';
import PhoneSupport from '../common/PhoneSupport';
import PhoneSupportFooter from '../common/PhoneSupportFooter';
import { useAppSelector } from '../../reducers';
import { crmSuiteOwnershipLevel } from '../../selectors/ProductInventorySelectors';
import { STARTER as STARTER_TAB } from 'pricing-pages-ui/constants/routing/Tabs';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { OWNERSHIP_CHECK_FAILED } from '../../constants/ErrorCauses';
import ErrorPage from '../error-page/ErrorPage';
import BannerSection from '../banners/BannerSection';
import SuiteFeatureTable from '../feature-table/suite-feature-table/SuiteFeatureTable';

const SuitePage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('crmSuite');
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const {
    tab
  } = usePageParams();
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: Screens.SUITE,
      tab
    });
  }, [tab]);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const marketingHighlightedProducts = getHighlightsForProductLine(ProductLines.MARKETING);
  const filteredMarketingHighlightedProducts = marketingHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const salesHighlightedProducts = getHighlightsForProductLine(ProductLines.SALES);
  const filteredSalesHighlightedProducts = salesHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const serviceHighlightedProducts = getHighlightsForProductLine(ProductLines.SERVICE);
  const filteredServiceHighlightedProducts = serviceHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const operationsHighlightedProducts = getHighlightsForProductLine(ProductLines.OPERATIONS);
  const filteredOperationsHighlightedProducts = operationsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const cmsHighlightedProducts = getHighlightsForProductLine(ProductLines.CMS);
  const filteredCmsHighlightedProducts = cmsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const productsByProductLine = {
    [ProductLines.MARKETING]: filteredMarketingHighlightedProducts,
    [ProductLines.SALES]: filteredSalesHighlightedProducts,
    [ProductLines.SERVICE]: filteredServiceHighlightedProducts,
    [ProductLines.OPERATIONS]: filteredOperationsHighlightedProducts,
    [ProductLines.CMS]: filteredCmsHighlightedProducts
  };
  const ownedSuiteLevel = useAppSelector(crmSuiteOwnershipLevel);
  const initialProductLevel = ownedSuiteLevel ? orderedProductLevels.indexOf(ownedSuiteLevel) <= orderedProductLevels.indexOf(STARTER) ? STARTER : ownedSuiteLevel : !tab || tab === STARTER_TAB ? STARTER : PROFESSIONAL;

  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }

  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [hasPricingAndPurchasingSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: "SUITE_PAGE"
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: NavItemEnum.SUITE,
      children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
        children: [/*#__PURE__*/_jsx(BannerSection, {
          screen: Screens.SUITE
        }), /*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(ProductPageHeader, {
          children: [/*#__PURE__*/_jsx(ProductPageTitle, {
            nonProductIdentifier: NavItemEnum.SUITE
          }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
            nonProductIdentifier: NavItemEnum.SUITE
          })]
        }), /*#__PURE__*/_jsx(SuiteTiles, {
          selectedTab: tab
        }), /*#__PURE__*/_jsx(SuiteFeatureTable, {
          productsByProductLine: productsByProductLine,
          screen: Screens.SUITE,
          tab: tab
        }, tab), /*#__PURE__*/_jsx(PhoneSupport, {
          phoneSourceId: "crmsuite-middle",
          screen: Screens.SUITE
        }), /*#__PURE__*/_jsx(SuiteCalculator, {
          initialProductLevel: initialProductLevel
        }), /*#__PURE__*/_jsx(WhyGoHubSpotSection, {
          selectedNavItem: NavItemEnum.SUITE
        }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import(
          /* webpackChunkName: "public-chunk" */
          `pricing-pages-ui/components/company-proof/CompanyProof`),
          publicComponentProps: {
            tab
          }
        }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsxs(Faq, {
          children: [FaqsConfig[NavItemEnum.SUITE].map((_ref) => {
            let {
              questionKey
            } = _ref,
                passthroughOptions = _objectWithoutPropertiesLoose(_ref, ["questionKey"]);

            return /*#__PURE__*/_jsx(Question, Object.assign({
              questionKey: questionKey
            }, passthroughOptions), questionKey);
          }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
        }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
          phoneSourceId: "crmsuite-footer",
          screen: Screens.SUITE
        })]
      })
    }), /*#__PURE__*/_jsx(CompareStatusFooter, {})]
  });
};

export default SuitePage;